import { useState } from 'react';

type DeliverableType = 'missing' | 'validation' | 'difference' | 'archive';

export default function DeliverablesCenter() {
  const [activeTab, setActiveTab] = useState<DeliverableType>('missing');

  const tabs = [
    { id: 'missing' as DeliverableType, label: '缺资料清单', icon: 'ri-file-list-line' },
    { id: 'validation' as DeliverableType, label: '校验报告', icon: 'ri-shield-check-line' },
    { id: 'difference' as DeliverableType, label: '三流差异留痕', icon: 'ri-git-branch-line' },
    { id: 'archive' as DeliverableType, label: '归档包目录', icon: 'ri-archive-line' },
  ];

  const content = {
    missing: {
      title: '缺资料清单',
      desc: '按批次/票号自动生成缺失资料清单,提前补齐避免退单',
      features: [
        '按票号/批次分组展示',
        '缺失字段明细标注',
        '补齐进度实时追踪',
        '历史记录可回溯',
      ],
      imagePrompt: 'A clean professional dashboard interface showing a missing documents checklist with organized sections, checkboxes, and status indicators, modern UI design with teal accent colors, white background, minimalist style, data table layout with clear typography, business software aesthetic',
    },
    validation: {
      title: '校验报告',
      desc: '提交前自动校验缺项/异常/逻辑冲突,减少返工',
      features: [
        '缺项/异常提前提示',
        '逻辑冲突自动检测',
        '校验规则可配置',
        '校验历史可导出',
      ],
      imagePrompt: 'A professional validation report interface with warning indicators, error messages, and success checkmarks, clean dashboard design with organized sections, teal and orange accent colors, white background, modern business software UI, data validation visualization with clear status indicators',
    },
    difference: {
      title: '三流差异留痕',
      desc: '平台流水/收款/报关口径对齐,差异标红留痕可追溯',
      features: [
        '三流数据自动对齐',
        '差异项标红提示',
        '处理记录完整留痕',
        '按票号关联查询',
      ],
      imagePrompt: 'A sophisticated data reconciliation interface showing three-way matching between platform data, payments, and customs declarations, with highlighted discrepancies in red, clean modern dashboard design with teal accents, white background, professional business software aesthetic, data flow visualization with connection lines',
    },
    archive: {
      title: '归档包目录',
      desc: '单证目录化归档,按票/批次/主体导出,查验时拿得出',
      features: [
        '目录结构清晰可视',
        '按票/批次/主体归档',
        '一键导出归档包',
        '版本管理与留存',
      ],
      imagePrompt: 'A professional document archive interface with folder structure, file organization system, and download options, clean modern UI design with teal accent colors, white background, minimalist business software aesthetic, hierarchical file tree view with clear labels and icons',
    },
  };

  const currentContent = content[activeTab];

  return (
    <section id="deliverables-center" className="py-16 bg-gray-50">
      <div className="max-w-[1400px] mx-auto px-6">
        {/* 标题 */}
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">交付物中心</h2>
          <p className="text-base text-gray-600 max-w-3xl mx-auto">
            把治理能力变得可视化,每个交付物都是合规管理的证据链
          </p>
        </div>

        {/* Tab 切换器 */}
        <div className="flex items-center justify-center gap-3 mb-10 flex-wrap">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-3 rounded-lg font-medium transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 ${
                activeTab === tab.id
                  ? 'bg-teal-600 text-white shadow-md'
                  : 'bg-white text-gray-700 border border-gray-200 hover:border-teal-300'
              }`}
            >
              <i className={`${tab.icon} text-lg`}></i>
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* 内容区 */}
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
            {/* 左侧：示意图 */}
            <div className="bg-gradient-to-br from-gray-50 to-white p-8 flex items-center justify-center">
              <div className="w-full max-w-[500px]">
                <img
                  src={`https://public.readdy.ai/ai/img_res/948a13f1eb402f8ff16472e217608fb6.jpg$%7BencodeURIComponent%28%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20currentContent.imagePrompt%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%29%7D&width=500&height=400&seq=${activeTab}-deliverable-demo&orientation=landscape`}
                  alt={currentContent.title}
                  className="w-full h-auto rounded-lg shadow-lg object-cover"
                  style={{ aspectRatio: '5/4' }}
                />
              </div>
            </div>

            {/* 右侧：说明与功能 */}
            <div className="p-8 flex flex-col justify-between">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-teal-50 text-teal-600">
                    <i className={`${tabs.find((t) => t.id === activeTab)?.icon} text-xl`}></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">{currentContent.title}</h3>
                </div>

                <p className="text-base text-gray-600 mb-8 leading-relaxed">
                  {currentContent.desc}
                </p>

                {/* 功能特性 */}
                <div className="space-y-3 mb-8">
                  {currentContent.features.map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <div className="w-5 h-5 flex items-center justify-center rounded-full bg-teal-50 text-teal-600 mt-0.5 flex-shrink-0">
                        <i className="ri-check-line text-sm"></i>
                      </div>
                      <span className="text-sm text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* 操作按钮 */}
              <div className="flex items-center gap-4 pt-6 border-t border-gray-100">
                <button
                  onClick={() => {
                    const event = new CustomEvent('openComplianceDrawer');
                    window.dispatchEvent(event);
                  }}
                  className="px-5 py-2.5 bg-teal-600 text-white text-sm font-medium rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  查看完整示例
                </button>
                <button
                  onClick={() => {
                    const event = new CustomEvent('openComplianceDrawer');
                    window.dispatchEvent(event);
                  }}
                  className="px-5 py-2.5 bg-white text-gray-700 border border-gray-300 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  下载样例文件
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* 底部说明 */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-500">
            交付物以产品版本与合同约定为准
          </p>
        </div>
      </div>
    </section>
  );
}